import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Scale, Loader2, Eye, EyeOff, KeyRound, ShieldCheck, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user, setUserData } = useAuth();
  const { preloadAllData } = useDataCache();
  const navigate = useNavigate();

  // Change password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 2FA modal state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorTempToken, setTwoFactorTempToken] = useState(null);
  const [twoFactorEmail, setTwoFactorEmail] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSetupAdmin = async () => {
    try {
      const response = await axios.post(`${API}/auth/setup-admin`);
      toast.success(`Admin created! Email: ${response.data.email}, Password: ${response.data.password}`);
      setEmail(response.data.email);
      setPassword(response.data.password);
    } catch (error) {
      if (error.response?.data?.detail === "Admin already exists") {
        toast.info("Admin already exists. Use admin@lawfirm.com / admin123");
        setEmail("admin@lawfirm.com");
        setPassword("admin123");
      } else {
        toast.error("Failed to setup admin");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result.requires_2fa) {
        setTwoFactorTempToken(result.temp_token);
        setTwoFactorEmail(result.email);
        setShow2FAModal(true);
        toast.info("Introduceți codul de verificare");
        return;
      }
      
      // Check if user must change password
      if (result.must_change_password) {
        setTempToken(result.token);
        setTempUser(result.user);
        setShowChangePasswordModal(true);
        toast.info("Trebuie să îți schimbi parola pentru a continua");
      } else {
        toast.success("Bine ai revenit!");
        // Preload data in background for faster navigation
        preloadAllData();
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credențiale invalide");
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error("Introduceți codul complet");
      return;
    }

    setIsVerifying2FA(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-login`, {
        email: twoFactorEmail,
        temp_token: twoFactorTempToken,
        code: twoFactorCode
      });
      
      const { token: newToken, user: userData, must_change_password } = response.data;
      
      localStorage.setItem("token", newToken);
      
      if (must_change_password) {
        setTempToken(newToken);
        setTempUser(userData);
        setShow2FAModal(false);
        setShowChangePasswordModal(true);
        toast.info("Trebuie să îți schimbi parola pentru a continua");
      } else {
        setUserData(userData);
        setShow2FAModal(false);
        toast.success("Bine ai revenit!");
        // Preload data in background for faster navigation
        preloadAllData();
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Completează ambele câmpuri");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Parola trebuie să aibă minim 6 caractere");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Parolele nu coincid");
      return;
    }

    setIsChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/force-change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      
      toast.success("Parola a fost schimbată cu succes!");
      setShowChangePasswordModal(false);
      setNewPassword("");
      setConfirmPassword("");
      
      // Now set the user to trigger navigation
      setUserData(tempUser);
      // Preload data in background for faster navigation
      preloadAllData();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center login-bg relative">
      <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md mx-4 relative z-10 shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <Scale className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="font-heading text-3xl">Legal<span className="text-teal-600">Flow</span></CardTitle>
          <p className="text-sm text-muted-foreground">by Dan Codrean</p>
          <CardDescription className="font-body text-muted-foreground mt-2">
            Law Firm Case Management System
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="font-body"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="font-body text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-body pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Ai uitat parola?
                </Link>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full font-body font-medium"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t">
            <Button
              variant="outline"
              className="w-full font-body text-sm"
              onClick={handleSetupAdmin}
              data-testid="setup-admin-button"
            >
              Setup Admin Account (First Time)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal pentru schimbarea parolei */}
      <Dialog open={showChangePasswordModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-modal">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-2">
              <KeyRound className="w-6 h-6 text-amber-600" />
            </div>
            <DialogTitle className="text-center">Schimbare Parolă Obligatorie</DialogTitle>
            <DialogDescription className="text-center">
              Ai folosit o parolă temporară. Te rugăm să setezi o parolă nouă pentru a continua.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Parolă Nouă</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Introdu parola nouă"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                  data-testid="new-password-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmă Parola</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirmă parola nouă"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                  data-testid="confirm-password-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Parola trebuie să aibă minim 6 caractere
              </p>
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={isChangingPassword}
              data-testid="change-password-submit"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Salvează Parola Nouă
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* 2FA Verification Modal */}
      <Dialog open={show2FAModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-login-modal">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <DialogTitle className="text-center">Verificare în Doi Pași</DialogTitle>
            <DialogDescription className="text-center">
              Introduceți codul din aplicația de autentificare sau un cod de backup
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handle2FAVerify} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="twoFactorCode">Cod de Verificare</Label>
              <Input
                id="twoFactorCode"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase())}
                placeholder="000000 sau cod backup"
                className="text-center text-xl tracking-widest"
                data-testid="2fa-code-input"
                autoFocus
              />
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={isVerifying2FA}
              data-testid="2fa-verify-submit"
            >
              {isVerifying2FA ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificare...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Verifică
                </>
              )}
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setShow2FAModal(false);
                setTwoFactorCode("");
              }}
            >
              Anulează
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
